/**
 * Pure trace builder for the asset-class-volatility chart.
 *
 * Used by both the MARS-Live VolatilityChart and the portal chat's
 * inline vol chart. Callers layer their own Plotly layout on top —
 * this module only owns the `data[]` shape.
 *
 * Vol values are stored as decimals (0.12 = 12%); the builder
 * multiplies by 100 for display. Pre-2000 rows are dropped (legacy
 * data hygiene rule from the live chart).
 */
import type { Data } from 'plotly.js';
import { hoverTemplate } from './hoverTemplates';

export interface VolPayload {
  dates: string[];
  /** Series keyed by the source-side asset name (varies by surface). */
  series: Record<string, Array<number | null>>;
  names?: string[];
}

export interface VolAsset {
  /** Series key in the payload (e.g. "Equities" for the .mat surface,
   *  "equities" for the QMAS DB surface). */
  seriesKey: string;
  /** Legend / hover label. */
  label: string;
  /** Brand colour. */
  color: string;
}

/**
 * One scatter line per asset, restricted to `[startDate, endDate]`.
 * Skips null / non-finite cells and any pre-2000 dates.
 */
export function buildAssetVolTraces(
  payload: VolPayload,
  opts: {
    assets: ReadonlyArray<VolAsset>;
    startDate?: string | null;
    endDate?: string | null;
    /** Trace line width; defaults to the historical 2. */
    lineWidth?: number;
  },
): Data[] {
  const out: Data[] = [];
  for (const a of opts.assets) {
    const arr = payload.series[a.seriesKey];
    if (!arr) continue;
    const x: string[] = [];
    const y: number[] = [];
    for (let i = 0; i < payload.dates.length; i++) {
      const d = payload.dates[i];
      if (opts.startDate && d < opts.startDate) continue;
      if (opts.endDate && d > opts.endDate) continue;
      if (new Date(d).getFullYear() < 2000) continue;
      const v = arr[i];
      if (v == null || !Number.isFinite(v)) continue;
      x.push(d);
      y.push(v * 100);
    }
    if (!x.length) continue;
    out.push({
      type: 'scatter',
      mode: 'lines',
      name: a.label,
      x,
      y,
      line: { color: a.color, width: opts.lineWidth ?? 2 },
      hovertemplate: hoverTemplate({ name: a.label, format: '.1f', suffix: '%' }),
    });
  }
  return out;
}
